/**
 * Call Center Supervisor Dashboard
 * Real-time monitoring and analytics for managers
 * Shows agent status, queue metrics, and performance data
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Phone, CheckCircle, XCircle, Clock, Star, Filter, Download } from 'lucide-react';

export default function SupervisorDashboard() {
  const [agents, setAgents] = useState<any[]>([]);
  const [activeCalls, setActiveCalls] = useState<any[]>([]);
  const [queueMetrics, setQueueMetrics] = useState<any[]>([]);
  const [todayStats, setTodayStats] = useState({
    totalCalls: 0,
    answeredCalls: 0,
    abandonedCalls: 0,
    avgWaitTime: 0,
    avgQualityScore: 0
  });

  // Rejection Analytics State
  const [rejections, setRejections] = useState<any[]>([]);
  const [filteredRejections, setFilteredRejections] = useState<any[]>([]);
  const [allAgentsList, setAllAgentsList] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    agentId: 'all',
    reason: 'all',
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [rejectionStats, setRejectionStats] = useState({
    total: 0,
    byReason: {} as Record<string, number>,
    byAgent: {} as Record<string, number>
  });

  // Load agents
  useEffect(() => {
    loadAgents();
    loadActiveCalls();
    loadQueueMetrics();
    loadTodayStats();
    loadRejections();

    // Refresh every 5 seconds
    const interval = setInterval(() => {
      loadAgents();
      loadActiveCalls();
      loadQueueMetrics();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Filter rejections when filters change
  useEffect(() => {
    filterRejections();
  }, [rejections, filters]);

  const loadAgents = async () => {
    const { data } = await supabase
      .from('agents')
      .select('*')
      .eq('is_active', true)
      .order('current_status', { ascending: false });
    
    setAgents(data || []);
    setAllAgentsList(data || []);
  };

  const loadActiveCalls = async () => {
    const { data } = await supabase
      .from('calls')
      .select(`
        *,
        call_center_agents(first_name, last_name, extension),
        people(first_name, last_name, phone)
      `)
      .in('status', ['ringing', 'in_progress'])
      .order('started_at', { ascending: true });
    
    setActiveCalls(data || []);
  };

  const loadQueueMetrics = async () => {
    const { data } = await supabase
      .from('call_queues')
      .select('*')
      .eq('is_active', true);
    
    setQueueMetrics(data || []);
  };

  const loadTodayStats = async () => {
    // Calculate stats directly from calls table since call_analytics_daily doesn't exist yet
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    const { data: calls } = await supabase
      .from('calls')
      .select('status, queue_time_seconds, quality_score, answered_at, started_at')
      .gte('started_at', todayStart.toISOString());
    
    if (calls && calls.length > 0) {
      const totalCalls = calls.length;
      const answeredCalls = calls.filter(c => c.answered_at !== null).length;
      const abandonedCalls = calls.filter(c => c.status === 'canceled' || c.status === 'no_answer').length;
      
      const totalWaitTime = calls.reduce((sum, c) => sum + (c.queue_time_seconds || 0), 0);
      const avgWaitTime = totalCalls > 0 ? Math.round(totalWaitTime / totalCalls) : 0;
      
      const qualityScores = calls.filter(c => c.quality_score !== null).map(c => c.quality_score);
      const avgQualityScore = qualityScores.length > 0 
        ? qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length 
        : 0;
      
      setTodayStats({
        totalCalls,
        answeredCalls,
        abandonedCalls,
        avgWaitTime,
        avgQualityScore
      });
    }
  };

  const loadRejections = async () => {
    const { data } = await supabase
      .from('call_rejections')
      .select(`
        *,
        call_center_agents(first_name, last_name, extension)
      `)
      .order('rejected_at', { ascending: false })
      .limit(500);
    
    setRejections(data || []);
  };

  const filterRejections = () => {
    let filtered = [...rejections];

    // Filter by agent
    if (filters.agentId !== 'all') {
      filtered = filtered.filter(r => r.agent_id === filters.agentId);
    }

    // Filter by reason
    if (filters.reason !== 'all') {
      filtered = filtered.filter(r => r.reason === filters.reason);
    }

    // Filter by date range
    filtered = filtered.filter(r => {
      const rejectedDate = new Date(r.rejected_at).toISOString().split('T')[0];
      return rejectedDate >= filters.startDate && rejectedDate <= filters.endDate;
    });

    setFilteredRejections(filtered);

    // Calculate stats
    const total = filtered.length;
    const byReason: Record<string, number> = {};
    const byAgent: Record<string, number> = {};

    filtered.forEach(r => {
      byReason[r.reason] = (byReason[r.reason] || 0) + 1;
      const agentName = `${r.call_center_agents?.first_name} ${r.call_center_agents?.last_name}`;
      byAgent[agentName] = (byAgent[agentName] || 0) + 1;
    });

    setRejectionStats({ total, byReason, byAgent });
  };

  const exportRejections = () => {
    const csv = [
      ['Date', 'Time', 'Agent', 'Call SID', 'Reason', 'Custom Reason'].join(','),
      ...filteredRejections.map(r => [
        new Date(r.rejected_at).toLocaleDateString(),
        new Date(r.rejected_at).toLocaleTimeString(),
        `${r.call_center_agents?.first_name} ${r.call_center_agents?.last_name}`,
        r.call_sid,
        r.reason,
        r.custom_reason || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-rejections-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
  };

  const bargeIn = async (callId: string) => {
    console.log('Supervisor barging into call:', callId);
    // TODO: Implement Twilio conference barge-in
  };

  return (
    <AdminPageLayout
      title="Call Center Supervisor Dashboard"
      subtitle="Real-time monitoring and analytics"
    >
      {/* Key Metrics */}
      <StatsGrid cols={5}>
        <StatCard
          label="Total Calls Today"
          value={todayStats.totalCalls}
          icon={Phone}
        />
        <StatCard
          label="Answered"
          value={todayStats.answeredCalls}
          icon={CheckCircle}
          variant="success"
        />
        <StatCard
          label="Abandoned"
          value={todayStats.abandonedCalls}
          icon={XCircle}
          variant="critical"
        />
        <StatCard
          label="Avg Wait Time"
          value={`${todayStats.avgWaitTime}s`}
          icon={Clock}
        />
        <StatCard
          label="Quality Score"
          value={todayStats.avgQualityScore.toFixed(1)}
          icon={Star}
        />
      </StatsGrid>

      {/* Agent Status Grid */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Agent Status</h2>
          <div className="grid grid-cols-4 gap-4">
            {agents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} />
            ))}
        </div>
      </Card>

      {/* Active Calls */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Active Calls ({activeCalls.length})
          </h2>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
              Live
            </span>
          </div>
        </div>
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Call</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeCalls.map((call) => (
                <tr key={call.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{call.from_number}</div>
                    <div className="text-xs text-gray-500">{call.call_sid.substring(0, 20)}...</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {call.call_center_agents?.first_name} {call.call_center_agents?.last_name}
                    </div>
                    <div className="text-xs text-gray-500">Ext: {call.call_center_agents?.extension}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {call.people ? (
                      <>
                        <div className="text-sm text-gray-900">
                          {call.people.first_name} {call.people.last_name}
                        </div>
                        <div className="text-xs text-gray-500">{call.people.phone}</div>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">Unknown</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {call.call_type || 'General'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDuration(call.duration_seconds || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      call.status === 'in_progress' ? 'bg-green-100 text-green-800' :
                      call.status === 'ringing' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {call.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => bargeIn(call.id)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Listen/Barge
                    </button>
                  </td>
                </tr>
              ))}
              {activeCalls.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-400">
                    No active calls
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Queue Status */}
      <div className="grid grid-cols-3 gap-4">
        {queueMetrics.map((queue) => (
          <QueueCard key={queue.id} queue={queue} />
        ))}
      </div>

      {/* Call Rejection Analytics */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Call Rejection Analytics</h2>
            <p className="text-sm text-gray-500 mt-1">Track and analyze why agents reject incoming calls</p>
          </div>
          <button
            onClick={exportRejections}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="w-4 h-4 inline mr-1" />
              Agent
            </label>
            <select
              value={filters.agentId}
              onChange={(e) => setFilters({ ...filters, agentId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Agents</option>
              {allAgentsList.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.first_name} {agent.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
            <select
              value={filters.reason}
              onChange={(e) => setFilters({ ...filters, reason: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Reasons</option>
              <option value="Taking another call">Taking another call</option>
              <option value="On break">On break</option>
              <option value="Technical issue">Technical issue</option>
              <option value="Wrong queue">Wrong queue</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-sm text-red-600 font-medium">Total Rejections</div>
            <div className="text-3xl font-bold text-red-900 mt-2">{rejectionStats.total}</div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium">Most Common Reason</div>
            <div className="text-lg font-bold text-blue-900 mt-2">
              {Object.entries(rejectionStats.byReason).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
            </div>
            <div className="text-sm text-blue-600 mt-1">
              {Object.entries(rejectionStats.byReason).sort((a, b) => b[1] - a[1])[0]?.[1] || 0} times
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-sm text-purple-600 font-medium">Top Rejector</div>
            <div className="text-lg font-bold text-purple-900 mt-2">
              {Object.entries(rejectionStats.byAgent).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
            </div>
            <div className="text-sm text-purple-600 mt-1">
              {Object.entries(rejectionStats.byAgent).sort((a, b) => b[1] - a[1])[0]?.[1] || 0} rejections
            </div>
          </div>
        </div>

        {/* Rejection List */}
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Call SID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRejections.map((rejection) => (
                <tr key={rejection.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(rejection.rejected_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(rejection.rejected_at).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {rejection.call_center_agents?.first_name} {rejection.call_center_agents?.last_name}
                    </div>
                    <div className="text-xs text-gray-500">
                      Ext: {rejection.call_center_agents?.extension}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs text-gray-500 font-mono">
                      {rejection.call_sid}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      rejection.reason === 'Taking another call' ? 'bg-blue-100 text-blue-800' :
                      rejection.reason === 'On break' ? 'bg-yellow-100 text-yellow-800' :
                      rejection.reason === 'Technical issue' ? 'bg-red-100 text-red-800' :
                      rejection.reason === 'Wrong queue' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {rejection.reason}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {rejection.custom_reason ? (
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {rejection.custom_reason}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredRejections.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                    No rejections found for selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminPageLayout>
  );
}

// Agent Card Component
type AgentStatus = 'available' | 'busy' | 'break' | 'offline';

interface AgentCardProps {
  agent: {
    first_name?: string;
    last_name?: string;
    extension?: string;
    total_calls_handled?: number;
    average_handle_time_seconds?: number;
    status: AgentStatus | string;
  };
}

function AgentCard({ agent }: AgentCardProps) {
  const statusColors: Record<AgentStatus, string> = {
    available: 'bg-green-100 border-green-500 text-green-800',
    busy: 'bg-red-100 border-red-500 text-red-800',
    break: 'bg-yellow-100 border-yellow-500 text-yellow-800',
    offline: 'bg-gray-100 border-gray-300 text-gray-600'
  };

  const status = (agent.status ?? 'offline') as AgentStatus;
  const statusClass = statusColors[status] || statusColors.offline;

  return (
    <div className={`border-2 rounded-lg p-4 ${statusClass}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium">{agent.first_name} {agent.last_name}</div>
        <span className="text-xs font-bold uppercase">{agent.status}</span>
      </div>
      <div className="text-sm opacity-75">
        <div>Ext: {agent.extension || 'N/A'}</div>
        <div>Calls Today: {agent.total_calls_handled || 0}</div>
        <div>Avg Handle: {agent.average_handle_time_seconds || 0}s</div>
      </div>
    </div>
  );
}

// Queue Card Component
function QueueCard({ queue }: any) {
  return (
    <div className="bg-white border rounded-lg p-6">
      <h3 className="font-semibold text-gray-900 mb-4">{queue.display_name}</h3>
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Calls in Queue:</span>
          <span className="font-medium">{queue.calls_in_queue || 0}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Longest Wait:</span>
          <span className="font-medium">{formatDuration(queue.longest_wait_seconds || 0)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Avg Wait:</span>
          <span className="font-medium">{formatDuration(queue.average_wait_seconds || 0)}</span>
        </div>
      </div>
      {queue.calls_in_queue > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-xs text-yellow-600 font-medium">
            ⚠️ {queue.calls_in_queue} caller(s) waiting
          </div>
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
